import { useState, useEffect } from "react";

import stripAnsi from "strip-ansi";

import { SandboxOutputLine } from "./OutputTypes";

const useOutputInternal = (replUrl: string | null, relayUrl: string | null, isReady: boolean) => {
  const [outputLines, setOutputLines] = useState<SandboxOutputLine[]>([]);
  const [newLinesCount, setNewLinesCount] = useState<number>(0);

  // --- repl EventSource ---
  useEffect(() => {
    if (!replUrl || !isReady) {
      return;
    }

    const source = new EventSource(`${replUrl}/repl/eval`, { withCredentials: true });

    source.onerror = () => {
      const readyStateMap = { 0: "CONNECTING", 1: "OPEN", 2: "CLOSED" } as const;
      const state = readyStateMap[source.readyState as keyof typeof readyStateMap] || "UNKNOWN";
      console.error(`[OutputHook] repl EventSource error (readyState: ${state}, url: ${replUrl})`);
    };

    const onMessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data) as { output: string; error?: boolean };
      if (typeof data.output === "string") {
        let count = 0;
        for (const line of data.output.split("\n")) {
          if (line.trim() === "") {
            continue;
          }
          setOutputLines(prev => [
            ...prev,
            {
              id: (prev.length > 0 ? parseInt(prev[prev.length - 1].id) + 1 : 1).toString(),
              timestamp: new Date(),
              text: stripAnsi(line),
            },
          ]);
          count++;
        }

        if (count > 0) {
          setNewLinesCount(prev => prev + count);
        }
      }
    };

    source.addEventListener("repl-errors", onMessage);

    return () => {
      source.removeEventListener("repl-errors", onMessage);
      source.close();
    };
  }, [replUrl, isReady]);

  // --- relay EventSource ---
  useEffect(() => {
    if (!relayUrl || !isReady) {
      return;
    }

    // The relay container streams real-time stdout from the sandbox app via its root
    // endpoint. Each SSE message contains a single line with a server-provided timestamp.
    const source = new EventSource(`${relayUrl}/`, { withCredentials: true });

    source.onerror = () => {
      const readyStateMap = { 0: "CONNECTING", 1: "OPEN", 2: "CLOSED" } as const;
      const state = readyStateMap[source.readyState as keyof typeof readyStateMap] || "UNKNOWN";
      console.error(
        `[OutputHook] relay EventSource error (readyState: ${state}, url: ${relayUrl})`
      );
    };

    const onMessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data) as { line: string; timestamp: string };
      if (typeof data.line === "string" && typeof data.timestamp === "string") {
        const timestamp = new Date(data.timestamp);
        if (!isNaN(timestamp.getTime())) {
          setOutputLines(prev => [
            ...prev,
            {
              id: (prev.length > 0 ? parseInt(prev[prev.length - 1].id) + 1 : 1).toString(),
              timestamp,
              text: stripAnsi(data.line),
            },
          ]);
          setNewLinesCount(prev => prev + 1);
        }
      }
    };

    source.addEventListener("message", onMessage);

    return () => {
      source.removeEventListener("message", onMessage);
      source.close();
    };
  }, [relayUrl, isReady]);

  const resetNewLinesCount = () => {
    setNewLinesCount(0);
  };

  const clearOutput = () => {
    setOutputLines([]);
    setNewLinesCount(0);
  };

  return { outputLines, newLinesCount, resetNewLinesCount, clearOutput };
};

export default useOutputInternal;
